import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"
import { cn } from "../../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-salt-gold-dark focus-visible:ring-offset-white dark:focus-visible:ring-offset-salt-navy-dark disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        // Primary button: SALT Navy
        default: "bg-salt-navy text-white hover:bg-salt-navy-dark shadow-md hover:shadow-lg active:scale-[0.98]",
        // Alias for legacy common/Button APIs
        primary: "bg-salt-navy text-white hover:bg-salt-navy-dark shadow-md hover:shadow-lg active:scale-[0.98]",
        // Accent/CTA button: SALT Gold
        accent: "bg-salt-gold text-salt-navy hover:bg-salt-gold-dark shadow-md hover:shadow-lg active:scale-[0.98]",
        destructive:
          "bg-red-600 text-white hover:bg-red-700 shadow-md hover:shadow-lg active:scale-[0.98]",
        danger:
          "bg-red-600 text-white hover:bg-red-700 shadow-md hover:shadow-lg active:scale-[0.98]",
        outline:
          "border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 active:scale-[0.98]",
        secondary:
          "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600 active:scale-[0.98]",
        ghost: "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-900 dark:text-white active:scale-[0.98]",
        link: "text-salt-navy dark:text-salt-gold underline-offset-4 hover:underline",
        // Brand gradient: Navy -> Gold
        gradient: "bg-gradient-to-r from-salt-navy to-salt-gold text-white hover:from-salt-navy-dark hover:to-salt-gold-dark shadow-lg hover:shadow-xl active:scale-[0.98]",
      },
      size: {
        default: "h-11 px-6 py-2.5",
        md: "h-11 px-6 py-2.5",
        sm: "h-9 rounded-md px-4 text-sm",
        lg: "h-12 rounded-xl px-8 text-base",
        icon: "h-10 w-10 rounded-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /** Shows spinner and disables the button */
  loading?: boolean
  /** Icon before label */
  icon?: React.ReactNode
  /** Icon after label */
  iconRight?: React.ReactNode
  /** Stretch to full container width */
  fullWidth?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      icon,
      iconRight,
      fullWidth = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button"
    const isDisabled = disabled || loading
    const classes = cn(
      buttonVariants({ variant, size }),
      fullWidth && "w-full",
      className
    )

    // Slot (asChild) requires a single child — skip loading/icon composition
    if (asChild) {
      return (
        <Comp className={classes} ref={ref} {...props}>
          {children}
        </Comp>
      )
    }

    return (
      <Comp
        className={classes}
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin mr-2" aria-hidden="true" />
            <span>Loading...</span>
          </>
        ) : (
          <>
            {icon && (
              <span className="mr-2 inline-flex" aria-hidden="true">
                {icon}
              </span>
            )}
            {children}
            {iconRight && (
              <span className="ml-2 inline-flex" aria-hidden="true">
                {iconRight}
              </span>
            )}
          </>
        )}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
